/**
 * E06-F01-T13 — Intake "Link to project" search-with-autocomplete.
 *
 * Replaces the flat `<select>` of every project. Controlled by the parent's
 * `value` (project id or null = Standalone). When idle the input shows the
 * linked project's label; focusing it starts a search (client / role / ID —
 * rules in projectPickerModel.ts). Picking an option links it, the "Standalone"
 * row (only when not `required`, E06-F01-T16) or the clear (×) button unlinks;
 * Escape / an outside click closes the
 * dropdown WITHOUT changing the link. Arrow keys + Enter navigate the options.
 */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from 'react';
import {
  filterProjectOptions,
  projectLabel,
  type ProjectPickOption,
} from './projectPickerModel.js';

export interface ProjectAutocompleteProps {
  /** Linked project id, or null for a standalone request. */
  readonly value: number | null;
  /** Projects to search (GET /projects). */
  readonly projects: readonly ProjectPickOption[];
  /** Fired with the picked project id, or null when unlinked. */
  readonly onChange: (projectId: number | null) => void;
  /** Test-id prefix. */
  readonly testId?: string;
  /** Whether the projects list is still loading. */
  readonly loading?: boolean;
  /**
   * E06-F01-T16 — the link is mandatory: no "No project linked" row is offered
   * and an empty value is flagged invalid up front (before any submit).
   */
  readonly required?: boolean;
}

// Only offered when the link is optional (`required` = false).
const STANDALONE_LABEL = '— No project linked —';

export function ProjectAutocomplete({
  value,
  projects,
  onChange,
  testId = 'intake-linked-project',
  loading = false,
  required = false,
}: ProjectAutocompleteProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Highlighted row: -1 = the Standalone row, 0..n-1 = the filtered projects.
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selected = value == null ? undefined : projects.find((p) => p.id === value);
  const selectedLabel =
    value == null ? '' : selected ? projectLabel(selected) : `Project #${value}`;
  const { items, total } = filterProjectOptions(projects, query);
  const invalid = required && value == null;
  // Lowest highlightable row: -1 (Standalone) only when the link is optional.
  const minActive = required ? 0 : -1;

  function openSearch(): void {
    setQuery('');
    setActive(minActive);
    setOpen(true);
  }

  function pick(id: number | null): void {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      openSearch();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, minActive));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const id = active >= 0 ? (items[active]?.id ?? null) : null;
      // A required link is never cleared by Enter (nothing to pick = no-op).
      if (required && id == null) return;
      pick(id);
    }
  }

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label="Link to project"
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        data-testid={`${testId}-input`}
        data-project-id={value ?? ''}
        data-invalid={invalid ? 'true' : undefined}
        placeholder={loading ? 'Loading projects…' : 'Search by client, role or project ID…'}
        className={`w-full border rounded-md p-2 pr-8 text-sm outline-none ${
          invalid
            ? 'border-red-500 bg-red-50 focus:border-red-600'
            : 'border-[var(--color-border)] bg-white focus:border-[var(--color-accent)]'
        }`}
        value={open ? query : selectedLabel}
        onFocus={openSearch}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(e.target.value.trim() ? 0 : minActive);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {value != null && !open ? (
        <button
          type="button"
          aria-label="Unlink project"
          data-testid={`${testId}-clear`}
          onClick={() => pick(null)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
        >
          ×
        </button>
      ) : null}
      {open ? (
        <ul
          role="listbox"
          data-testid={`${testId}-dropdown`}
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-[var(--color-border)] bg-white shadow-lg"
        >
          {required ? null : (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={active === -1}
                data-testid={`${testId}-standalone`}
                onClick={() => pick(null)}
                className={`block w-full px-3 py-1.5 text-left text-sm text-[var(--color-text-muted)] hover:bg-slate-50 ${active === -1 ? 'bg-slate-100' : ''}`}
              >
                {STANDALONE_LABEL}
              </button>
            </li>
          )}
          {items.map((p, i) => (
            <li key={p.id}>
              <button
                type="button"
                role="option"
                aria-selected={active === i}
                data-testid={`${testId}-option`}
                data-project-id={p.id}
                onClick={() => pick(p.id)}
                className={`block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50 ${active === i ? 'bg-slate-100' : ''} ${p.id === value ? 'font-semibold' : ''}`}
              >
                {projectLabel(p)}
              </button>
            </li>
          ))}
          {items.length === 0 && query.trim() ? (
            <li
              data-testid={`${testId}-empty`}
              className="px-3 py-1.5 text-sm text-[var(--color-text-muted)]"
            >
              No projects match “{query.trim()}”
            </li>
          ) : null}
          {total > items.length ? (
            <li className="px-3 py-1.5 text-xs text-[var(--color-text-muted)]">
              {total - items.length} more — keep typing to narrow
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

export default ProjectAutocomplete;
